import BreadCrumb from "Common/BreadCrumb";
import { useFormik } from "formik";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  CreditCard,
  Package,
  Plus,
  Trash2,
  User,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import type { AppDispatch } from "../../store";
import { RootState } from "../../slices";
import { Customer } from "../../slices/customers/reducer";
import { addOrders, getOrders, updateOrders } from "../../slices/orders/thunk";
import { PaymentForm } from "../../slices/paymentForms/reducer";
import { PaymentMethod } from "../../slices/paymentMethods/reducer";
import { Product } from "../../slices/products/reducer";

interface OrderItem {
  documentOwner: string;
  idDav: number | string;
  idProduct: string;
  id?: number | string;
  quantity: number | string;
  unitPrice: number | string;
  promotionPrice: number | string;
  promotionProrated: number | string;
  observation: string;
}

interface OrderFormValues {
  dateEmission: string;
  status: number | string;
  amountProducts: number | string;
  amountPromotion: number | string;
  observation: string;
  customerId: string;
  paymentMethodId: string;
  paymentFormId: string;
  items: OrderItem[];
}

// Componente para campo com validação melhorada
const FormField = ({
  label,
  icon,
  required = false,
  error,
  touched,
  children,
  helpText,
}: {
  label: string;
  icon?: React.ReactNode;
  required?: boolean;
  error?: string;
  touched?: boolean;
  children: React.ReactNode;
  helpText?: string;
}) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
      {icon}
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {children}
      {touched && error && (
        <div className="flex items-center gap-1 mt-1 text-xs text-red-600 duration-200 animate-in slide-in-from-left-2">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
      {touched && !error && (
        <CheckCircle2 className="absolute w-4 h-4 text-green-500 -translate-y-1/2 right-3 top-1/2" />
      )}
    </div>
    {helpText && <p className="text-xs text-slate-500 dark:text-slate-400">{helpText}</p>}
  </div>
);

// Skeleton Loading Component
let idCounter = 0;
const generateUniqueId = () => Date.now() * 1000 + (idCounter++ % 1000);

const SkeletonSelect = () => (
  <div className="animate-pulse">
    <div className="h-10 rounded-md bg-slate-200 dark:bg-slate-700"></div>
  </div>
);

const OrderForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isEdit = !!id;

  const loginUser = useSelector((state: RootState) => state.Login);
  const loggedUser = (loginUser as { user?: { documentOwner?: string; id?: number } }).user;
  const documentOwner = loggedUser?.documentOwner || "";
  const sellerId = loggedUser?.id || 0;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState({
    customers: true,
    products: true,
    paymentMethods: true,
    paymentForms: true,
    order: false,
  });
  const [productSearch, setProductSearch] = useState("");
  const [stockWarnings, setStockWarnings] = useState<Record<string, string>>({});
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: string; label: string }>>([
    { value: "", label: "Carregando clientes..." },
  ]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<
    Array<{ value: string; label: string }>
  >([{ value: "", label: "Carregando métodos..." }]);
  const [paymentFormOptions, setPaymentFormOptions] = useState<
    Array<{ value: string; label: string }>
  >([{ value: "", label: "Carregando formas..." }]);
  const [productOptions, setProductOptions] = useState<
    Array<{
      value: string;
      label: string;
      price?: number;
      promotionPrice?: number;
      quantity?: number;
    }>
  >([{ value: "", label: "Carregando produtos..." }]);

  const { customers } = useSelector((state: RootState) => state.Customers);
  const { paymentMethods } = useSelector((state: RootState) => state.PaymentMethods);
  const { paymentForms } = useSelector((state: RootState) => state.PaymentForms);
  const { products } = useSelector((state: RootState) => state.Products);
  const { orders } = useSelector((state: RootState) => state.Orders);

  const currentOrder = isEdit && id ? orders.find((order) => order.id === Number(id)) : null;

  useEffect(() => {
    if (isEdit && (orders.length === 0 || !currentOrder)) {
      dispatch(getOrders());
    }
  }, [dispatch, isEdit, orders.length, currentOrder]);

  useEffect(() => {
    if (isEdit && currentOrder) {
      validation.setValues({
        dateEmission: currentOrder.dateEmission.slice(0, 16),
        status: currentOrder.status,
        amountProducts: currentOrder.amountProducts.toString(),
        amountPromotion: currentOrder.amountPromotion.toString(),
        observation: currentOrder.observation || "",
        customerId: currentOrder.customerId.toString(),
        paymentMethodId: currentOrder.paymentMethodId.toString(),
        paymentFormId: currentOrder.paymentFormId.toString(),
        items: currentOrder.items.map((item) => ({
          documentOwner: item.documentOwner,
          idDav: item.idDav.toString(),
          idProduct: item.idProduct.toString(),
          id: item.id,
          quantity: item.quantity.toString(),
          unitPrice: item.unitPrice.toString(),
          promotionPrice: item.promotionPrice.toString(),
          promotionProrated: item.promotionProrated?.toString() || "0",
          observation: item.observation || "",
        })),
      });
    }
  }, [isEdit, currentOrder]);

  useEffect(() => {
    const loadCustomers = () => {
      setLoadingStates((prev) => ({ ...prev, customers: true }));

      let customersData = customers;

      if (!customersData || customersData.length === 0) {
        try {
          const davData = localStorage.getItem("davData");
          if (davData) {
            const parsedData = JSON.parse(davData);
            customersData = parsedData.customers || [];
          }
        } catch (error) {
          console.error("Erro ao buscar customers do localStorage:", error);
          customersData = [];
        }
      }

      if (customersData && customersData.length > 0) {
        const customerOptionsData = customersData
          .map((customer: Customer) => ({
            value: customer.id.toString(),
            label: `${customer.name}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

        const options = [
          { value: "", label: "Selecione um cliente/fornecedor" },
          ...customerOptionsData,
        ];
        setCustomerOptions(options);
      } else {
        setCustomerOptions([{ value: "", label: "Nenhum cliente encontrado" }]);
      }

      setLoadingStates((prev) => ({ ...prev, customers: false }));
    };

    const loadPaymentMethods = () => {
      let paymentMethodsData = paymentMethods;

      if (!paymentMethodsData || paymentMethodsData.length === 0) {
        try {
          const davData = localStorage.getItem("davData");
          if (davData) {
            const parsedData = JSON.parse(davData);
            paymentMethodsData = parsedData.paymentMethods || [];
          }
        } catch (error) {
          console.error("Erro ao buscar paymentMethods do localStorage:", error);
          paymentMethodsData = [];
        }
      }

      if (paymentMethodsData && paymentMethodsData.length > 0) {
        const paymentMethodOptionsData = paymentMethodsData
          .map((method: PaymentMethod) => ({
            value: method.id.toString(),
            label: method.description,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

        const options = [{ value: "", label: "Selecione um método" }, ...paymentMethodOptionsData];
        setPaymentMethodOptions(options);
      } else {
        setPaymentMethodOptions([{ value: "", label: "Nenhum método encontrado" }]);
      }

      setLoadingStates((prev) => ({ ...prev, paymentMethods: false }));
    };

    const loadPaymentForms = () => {
      let paymentFormsData = paymentForms;

      if (!paymentFormsData || paymentFormsData.length === 0) {
        try {
          const davData = localStorage.getItem("davData");
          if (davData) {
            const parsedData = JSON.parse(davData);
            paymentFormsData = parsedData.paymentForms || [];
          }
        } catch (error) {
          console.error("Erro ao buscar paymentForms do localStorage:", error);
          paymentFormsData = [];
        }
      }

      if (paymentFormsData && paymentFormsData.length > 0) {
        const paymentFormOptionsData = paymentFormsData
          .map((form: PaymentForm) => ({
            value: form.id.toString(),
            label: form.description,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

        const options = [{ value: "", label: "Selecione uma forma" }, ...paymentFormOptionsData];
        setPaymentFormOptions(options);
      } else {
        setPaymentFormOptions([{ value: "", label: "Nenhuma forma encontrada" }]);
      }

      setLoadingStates((prev) => ({ ...prev, paymentForms: false }));
    };

    const loadProducts = () => {
      setLoadingStates((prev) => ({ ...prev, products: true }));

      let productsData = products;

      if (!productsData || productsData.length === 0) {
        try {
          const davData = localStorage.getItem("davData");
          if (davData) {
            const parsedData = JSON.parse(davData);
            productsData = parsedData.products || [];
          }
        } catch (error) {
          console.error("Erro ao buscar products do localStorage:", error);
          productsData = [];
        }
      }

      if (productsData && productsData.length > 0) {
        const productOptionsData = productsData
          .map((product: Product) => ({
            value: product.id.toString(),
            label: product.description,
            price: product.unitPrice,
            promotionPrice: product.promotionPrice,
            quantity: product.quantity,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

        const options = [{ value: "", label: "Selecione um produto" }, ...productOptionsData];
        setProductOptions(options);
      } else {
        setProductOptions([{ value: "", label: "Nenhum produto encontrado" }]);
      }

      setLoadingStates((prev) => ({ ...prev, products: false }));
    };

    loadCustomers();
    loadPaymentMethods();
    loadPaymentForms();
    loadProducts();
  }, [customers, paymentMethods, paymentForms, products]);

  // Filtrar produtos por busca
  const filteredProductOptions = useMemo(() => {
    if (!productSearch.trim()) return productOptions;

    return productOptions.filter(
      (product) =>
        product.label.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.value.includes(productSearch)
    );
  }, [productOptions, productSearch]);

  const [currentItem, setCurrentItem] = useState<OrderItem>({
    documentOwner: documentOwner,
    idDav: "",
    idProduct: "",
    quantity: "",
    unitPrice: "",
    promotionPrice: "",
    promotionProrated: "",
    observation: "",
  });

  const validation = useFormik<OrderFormValues>({
    initialValues: {
      dateEmission: new Date().toISOString().slice(0, 16),
      status: 1,
      amountProducts: "",
      amountPromotion: "",
      observation: "",
      customerId: "",
      paymentMethodId: "",
      paymentFormId: "",
      items: [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      dateEmission: Yup.string().required("Data de emissão é obrigatória"),
      status: Yup.number()
        .oneOf([0, 1, 2], "Status deve ser 0, 1 ou 2")
        .required("Status é obrigatório"),
      amountProducts: Yup.number()
        .min(0, "Valor dos produtos deve ser positivo")
        .required("Valor dos produtos é obrigatório"),
      amountPromotion: Yup.number()
        .min(0, "Valor da promoção deve ser positivo")
        .required("Valor da promoção é obrigatório"),
      observation: Yup.string().max(500, "Observação deve ter no máximo 500 caracteres"),
      customerId: Yup.string().required("Cliente/Fornecedor é obrigatório"),
      paymentMethodId: Yup.string().required("Método de pagamento é obrigatório"),
      paymentFormId: Yup.string().required("Forma de pagamento é obrigatória"),
      items: Yup.array()
        .min(1, "Adicione pelo menos 1 item ao pedido")
        .required("Itens são obrigatórios"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);

      try {
        const orderId = isEdit ? Number(id) : generateUniqueId();
        const orderData = {
          documentOwner: documentOwner,
          id: orderId,
          sellerId: sellerId,
          customerId: Number(values.customerId),
          dateEmission: new Date(values.dateEmission).toISOString(),
          status: Number(values.status),
          amountProducts: Number(values.amountProducts),
          amountPromotion: Number(values.amountPromotion),
          observation: values.observation,
          paymentMethodId: Number(values.paymentMethodId),
          paymentFormId: Number(values.paymentFormId),
          items: values.items.map((item, index) => ({
            documentOwner: documentOwner,
            idDav: orderId,
            idProduct: Number(item.idProduct),
            id: index + 1,
            quantity: Number(item.quantity),
            unitPrice: Number(item.unitPrice),
            promotionPrice: Number(item.promotionPrice) || Number(item.unitPrice),
            promotionProrated: Number(item.promotionProrated) || 0,
            observation: item.observation || "",
          })),
        };

        // Adicionar campos de sincronização
        const orderWithSync = {
          ...orderData,
          synced: false,
          syncedAt: null,
          syncError: null,
          localCreatedAt: new Date().toISOString(),
        };

        if (isEdit) {
          await dispatch(updateOrders(orderWithSync));
          toast.success("Pedido atualizado com sucesso!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        } else {
          await dispatch(addOrders(orderWithSync));
          toast.success("Pedido criado com sucesso!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }

        setTimeout(() => {
          navigate("/sale-orders");
        }, 1500);
      } catch (error) {
        console.error("❌ Erro ao salvar pedido:", error);
        toast.error("Erro ao salvar pedido. Tente novamente.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleCancel = () => {
    navigate("/sale-orders");
  };

  const statusOptions = [
    { value: 0, label: "Pendente" },
    { value: 1, label: "Ativo" },
    { value: 2, label: "Cancelado" },
  ];

  const addItem = () => {
    if (!currentItem.idProduct || !currentItem.quantity || !currentItem.unitPrice) {
      toast.error("Preencha todos os campos obrigatórios do item", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
      return;
    }

    const newItem: OrderItem = {
      ...currentItem,
      documentOwner: documentOwner,
      id: generateUniqueId(),
      idDav: 0,
      quantity: Number(currentItem.quantity),
      unitPrice: Number(currentItem.unitPrice),
      promotionPrice: Number(currentItem.promotionPrice) || Number(currentItem.unitPrice),
      promotionProrated: Number(currentItem.promotionProrated) || 0,
    };

    const updatedItems = [...validation.values.items, newItem];
    validation.setFieldValue("items", updatedItems);

    setCurrentItem({
      documentOwner: documentOwner,
      idDav: "",
      idProduct: "",
      quantity: "",
      unitPrice: "",
      promotionPrice: "",
      promotionProrated: "",
      observation: "",
    });

    updateTotalAmounts(updatedItems);
  };

  const removeItem = (itemId: number | string | undefined) => {
    if (itemId === undefined) return;
    const currentItems = validation.values.items.filter(
      (item) => item.id !== undefined && item.id.toString() !== itemId.toString()
    );
    validation.setFieldValue("items", currentItems);
    updateTotalAmounts(currentItems);
  };

  // Calcular descontos automáticos baseados em regras
  const calculateAutomaticDiscounts = useCallback((itemsList: OrderItem[]) => {
    return itemsList.map((item) => {
      const quantity = Number(item.quantity);
      const unitPrice = Number(item.unitPrice);
      let finalPrice = unitPrice;

      // Regra: Desconto por quantidade (5% para 10+ unidades, 10% para 20+ unidades)
      if (quantity >= 20) {
        finalPrice = unitPrice * 0.9; // 10% desconto
      } else if (quantity >= 10) {
        finalPrice = unitPrice * 0.95; // 5% desconto
      }

      // Se já tem preço promocional definido, usar o menor preço
      const currentPromoPrice = Number(item.promotionPrice) || unitPrice;
      finalPrice = Math.min(finalPrice, currentPromoPrice);

      return {
        ...item,
        promotionPrice: finalPrice.toString(),
      };
    });
  }, []);

  const updateTotalAmounts = (itemsList: OrderItem[]) => {
    // Aplicar descontos automáticos
    const itemsWithDiscounts = calculateAutomaticDiscounts(itemsList);

    const totalProducts = itemsWithDiscounts.reduce((sum, item) => {
      return sum + Number(item.quantity) * Number(item.unitPrice);
    }, 0);

    const totalPromotion = itemsWithDiscounts.reduce((sum, item) => {
      const itemPromotion =
        Number(item.quantity) * Number(item.unitPrice) -
        Number(item.quantity) * Number(item.promotionPrice);
      return sum + Math.max(0, itemPromotion);
    }, 0);

    // Atualizar itens com os descontos aplicados
    validation.setFieldValue("items", itemsWithDiscounts);
    validation.setFieldValue("amountProducts", totalProducts.toFixed(2));
    validation.setFieldValue("amountPromotion", totalPromotion.toFixed(2));
  };

  const checkStock = useCallback(
    (productId: string, quantity: number) => {
      const product = productOptions.find((p) => p.value === productId);
      if (!product || !product.quantity) return;

      const availableStock = product.quantity;
      if (quantity > availableStock) {
        setStockWarnings((prev) => ({
          ...prev,
          [productId]: `Estoque insuficiente! Disponível: ${availableStock} unidades`,
        }));
      } else {
        setStockWarnings((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [productId]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [productOptions]
  );

  const handleProductChange = (productId: string) => {
    const product = productOptions.find((p) => p.value === productId);

    if (product && product.price && product.price > 0) {
      const unitPrice = product.price.toString();
      const promotionPrice = (
        product.promotionPrice && product.promotionPrice > 0
          ? product.promotionPrice
          : product.price
      ).toString();

      const newQuantity = 1;
      setCurrentItem((prev) => ({
        ...prev,
        idProduct: productId,
        quantity: newQuantity.toString(),
        unitPrice,
        promotionPrice,
      }));

      // Verificar estoque
      checkStock(productId, newQuantity);
    } else {
      setCurrentItem((prev) => ({
        ...prev,
        idProduct: productId,
        quantity: "1",
      }));

      if (productId) {
        checkStock(productId, 1);
      }
    }
  };
  return (
    <>
      <BreadCrumb title={isEdit ? "Editar Pedido" : "Novo Pedido"} pageTitle="Pedidos de Venda" />

      <div className="grid grid-cols-1 gap-x-5">
        <div className="card">
          <div className="card-body max-h-[calc(100vh-200px)] overflow-y-auto">
            <form onSubmit={validation.handleSubmit} className="space-y-8 pb-4">
              {/* Informações Básicas do Pedido */}
              <fieldset className="p-4 border rounded-lg border-slate-200 dark:border-zink-600 bg-slate-50/50 dark:bg-zink-800/50">
                <legend className="px-3 py-1 text-sm font-semibold bg-white border rounded-md text-slate-700 dark:text-slate-200 dark:bg-zink-700 border-slate-200 dark:border-zink-600">
                  Informações do Pedido
                </legend>
                <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField
                    label="Data de Emissão"
                    icon={<Calendar className="w-4 h-4" />}
                    required
                    error={validation.errors.dateEmission}
                    touched={validation.touched.dateEmission}
                    helpText="Data e hora da emissão do pedido"
                  >
                    <input
                      type="datetime-local"
                      name="dateEmission"
                      id="dateEmission"
                      className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 transition-colors ${
                        validation.touched.dateEmission && validation.errors.dateEmission
                          ? "border-red-300 focus:border-red-500"
                          : validation.touched.dateEmission && !validation.errors.dateEmission
                          ? "border-green-300 focus:border-green-500"
                          : ""
                      }`}
                      value={validation.values.dateEmission}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </FormField>

                  <FormField
                    label="Status"
                    icon={<AlertCircle className="w-4 h-4" />}
                    required
                    error={validation.errors.status}
                    touched={validation.touched.status}
                    helpText="Status atual do pedido"
                  >
                    <Select
                      className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      data-choices
                      name="status"
                      options={statusOptions}
                      value={statusOptions.find(
                        (option) => option.value === validation.values.status
                      )}
                      onChange={(selectedOption) => {
                        validation.setFieldValue("status", selectedOption?.value || "");
                      }}
                      onBlur={() => validation.setFieldTouched("status", true)}
                      placeholder="Selecione o status"
                    />
                  </FormField>

                  <div className="sm:col-span-2 lg:col-span-1">
                    <FormField
                      label="Observação"
                      helpText="Informações adicionais sobre o pedido (máximo 500 caracteres)"
                    >
                      <textarea
                        name="observation"
                        id="observation"
                        rows={3}
                        className={`resize-none form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 transition-colors ${
                          validation.touched.observation && validation.errors.observation
                            ? "border-red-300 focus:border-red-500"
                            : ""
                        }`}
                        placeholder="Observações adicionais sobre o pedido..."
                        value={validation.values.observation}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        maxLength={500}
                      />
                    </FormField>
                  </div>
                </div>
              </fieldset>

              {/* Valores do Pedido */}
              <fieldset className="p-4 border rounded-lg border-slate-200 dark:border-zink-600 bg-slate-50/50 dark:bg-zink-800/50">
                <legend className="px-3 py-1 text-sm font-semibold bg-white border rounded-md text-slate-700 dark:text-slate-200 dark:bg-zink-700 border-slate-200 dark:border-zink-600">
                  Valores
                </legend>
                <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
                  <FormField
                    label="Valor dos Produtos"
                    required
                    error={validation.errors.amountProducts}
                    touched={validation.touched.amountProducts}
                    helpText="Valor total dos produtos em R$"
                  >
                    <input
                      type="number"
                      name="amountProducts"
                      id="amountProducts"
                      disabled
                      className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 transition-colors ${
                        validation.touched.amountProducts && validation.errors.amountProducts
                          ? "border-red-300 focus:border-red-500"
                          : validation.touched.amountProducts && !validation.errors.amountProducts
                          ? "border-green-300 focus:border-green-500"
                          : ""
                      }`}
                      placeholder="0.00"
                      value={validation.values.amountProducts}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      min={0}
                      step={0.01}
                    />
                  </FormField>

                  <FormField
                    label="Valor da Promoção"
                    required
                    error={validation.errors.amountPromotion}
                    touched={validation.touched.amountPromotion}
                    helpText="Valor total das promoções em R$"
                  >
                    <input
                      type="number"
                      name="amountPromotion"
                      id="amountPromotion"
                      disabled
                      className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 transition-colors ${
                        validation.touched.amountPromotion && validation.errors.amountPromotion
                          ? "border-red-300 focus:border-red-500"
                          : validation.touched.amountPromotion && !validation.errors.amountPromotion
                          ? "border-green-300 focus:border-green-500"
                          : ""
                      }`}
                      placeholder="0.00"
                      value={validation.values.amountPromotion}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      min={0}
                      step={0.01}
                    />
                  </FormField>
                </div>
              </fieldset>

              {/* Cliente e Pagamento */}
              <fieldset className="p-4 border rounded-lg border-slate-200 dark:border-zink-600 bg-slate-50/50 dark:bg-zink-800/50">
                <legend className="px-3 py-1 text-sm font-semibold bg-white border rounded-md text-slate-700 dark:text-slate-200 dark:bg-zink-700 border-slate-200 dark:border-zink-600">
                  Cliente e Pagamento
                </legend>
                <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField
                    label="Cliente/Fornecedor"
                    icon={<User className="w-4 h-4" />}
                    required
                    error={validation.errors.customerId}
                    touched={validation.touched.customerId}
                    helpText="Selecione o cliente ou fornecedor do pedido"
                  >
                    {loadingStates.customers ? (
                      <SkeletonSelect />
                    ) : (
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        data-choices
                        name="customerId"
                        options={customerOptions}
                        value={customerOptions.find(
                          (option) => option.value === validation.values.customerId
                        )}
                        onChange={(selectedOption) => {
                          validation.setFieldValue("customerId", selectedOption?.value || "");
                        }}
                        onBlur={() => validation.setFieldTouched("customerId", true)}
                        placeholder="Selecione um cliente/fornecedor"
                      />
                    )}
                  </FormField>

                  <FormField
                    label="Método de Pagamento"
                    icon={<CreditCard className="w-4 h-4" />}
                    required
                    error={validation.errors.paymentMethodId}
                    touched={validation.touched.paymentMethodId}
                    helpText="Cartão, Dinheiro, PIX, etc."
                  >
                    {loadingStates.paymentMethods ? (
                      <SkeletonSelect />
                    ) : (
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        data-choices
                        name="paymentMethodId"
                        options={paymentMethodOptions}
                        value={paymentMethodOptions.find(
                          (option) => option.value === validation.values.paymentMethodId
                        )}
                        onChange={(selectedOption) => {
                          validation.setFieldValue("paymentMethodId", selectedOption?.value || "");
                        }}
                        onBlur={() => validation.setFieldTouched("paymentMethodId", true)}
                        placeholder="Selecione um método"
                      />
                    )}
                  </FormField>

                  <FormField
                    label="Forma de Pagamento"
                    icon={<Wallet className="w-4 h-4" />}
                    required
                    error={validation.errors.paymentFormId}
                    touched={validation.touched.paymentFormId}
                    helpText="À vista, parcelado, etc."
                  >
                    {loadingStates.paymentForms ? (
                      <SkeletonSelect />
                    ) : (
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        data-choices
                        name="paymentFormId"
                        options={paymentFormOptions}
                        value={paymentFormOptions.find(
                          (option) => option.value === validation.values.paymentFormId
                        )}
                        onChange={(selectedOption) => {
                          validation.setFieldValue("paymentFormId", selectedOption?.value || "");
                        }}
                        onBlur={() => validation.setFieldTouched("paymentFormId", true)}
                        placeholder="Selecione uma forma"
                      />
                    )}
                  </FormField>
                </div>
              </fieldset>

              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-custom-500" />
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
                    Itens do Pedido
                  </h3>
                </div>

                <div className="p-4 border rounded-lg border-slate-200 dark:border-zink-500 bg-slate-50 dark:bg-zink-800">
                  <h4 className="mb-3 text-base font-medium text-slate-600 dark:text-slate-300">
                    Adicionar Item
                  </h4>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                    <div>
                      <label className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Produto: <span className="text-red-500">*</span>
                      </label>
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        data-choices
                        name="currentProduct"
                        options={filteredProductOptions}
                        value={filteredProductOptions.find(
                          (option) => option.value === currentItem.idProduct
                        )}
                        onChange={(selectedOption) => {
                          if (selectedOption?.value) {
                            handleProductChange(selectedOption.value);
                          } else {
                            setCurrentItem((prev) => ({ ...prev, idProduct: "" }));
                          }
                        }}
                        onInputChange={(inputValue) => setProductSearch(inputValue)}
                        placeholder="Digite para buscar produtos..."
                        noOptionsMessage={() => "Nenhum produto encontrado"}
                        loadingMessage={() => "Carregando produtos..."}
                        isLoading={loadingStates.products}
                        isClearable
                        isSearchable
                      />
                    </div>

                    <div>
                      <label className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Quantidade: <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={currentItem.quantity}
                        onChange={(e) => {
                          const newQuantity = Number(e.target.value);
                          setCurrentItem((prev) => ({ ...prev, quantity: e.target.value }));

                          if (currentItem.idProduct && newQuantity > 0) {
                            checkStock(currentItem.idProduct, newQuantity);
                          }
                        }}
                        className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 ${
                          stockWarnings[currentItem.idProduct]
                            ? "border-red-300 focus:border-red-500"
                            : ""
                        }`}
                        placeholder="1"
                        min="1"
                      />
                      {stockWarnings[currentItem.idProduct] && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-red-600 duration-200 animate-in slide-in-from-left-2">
                          <AlertCircle className="w-3 h-3" />
                          {stockWarnings[currentItem.idProduct]}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Preço Unitário: <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={currentItem.unitPrice}
                        onChange={(e) =>
                          setCurrentItem((prev) => ({ ...prev, unitPrice: e.target.value }))
                        }
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Preço Promocional:
                      </label>
                      <input
                        type="number"
                        value={currentItem.promotionPrice}
                        onChange={(e) =>
                          setCurrentItem((prev) => ({ ...prev, promotionPrice: e.target.value }))
                        }
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <label className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Observação do Item:
                      </label>
                      <input
                        type="text"
                        value={currentItem.observation}
                        onChange={(e) =>
                          setCurrentItem((prev) => ({ ...prev, observation: e.target.value }))
                        }
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800"
                        placeholder="Informações adicionais sobre o item..."
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={addItem}
                        className="w-full px-4 py-2 text-white transition-all duration-200 border rounded-md bg-custom-500 border-custom-500 hover:bg-custom-600 focus:outline-none focus:ring-2 focus:ring-custom-500 focus:ring-offset-2 hover:shadow-lg hover:scale-105 active:scale-95"
                      >
                        <Plus className="inline w-4 h-4 mr-2" />
                        Adicionar Item
                      </button>
                    </div>
                  </div>
                </div>

                {validation.values.items.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-base font-medium text-slate-600 dark:text-slate-300">
                      Itens Adicionados ({validation.values.items.length})
                    </h4>

                    <div className="space-y-2">
                      {validation.values.items.map((item, index) => {
                        const product = productOptions.find((p) => p.value === item.idProduct);
                        const subtotal =
                          Number(item.quantity) * Number(item.promotionPrice || item.unitPrice);

                        return (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 transition-all duration-200 bg-white border rounded-md border-slate-200 dark:border-zink-500 dark:bg-zink-700 hover:shadow-md hover:border-custom-300 animate-in slide-in-from-left-2"
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            <div className="flex-1">
                              <p className="font-medium text-slate-700 dark:text-slate-200">
                                {product?.label || "Produto não encontrado"}
                              </p>
                              <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                                <span>Qtd: {item.quantity}</span>
                                <span>Unit.: R$ {Number(item.unitPrice).toFixed(2)}</span>
                                {item.promotionPrice &&
                                  Number(item.promotionPrice) !== Number(item.unitPrice) && (
                                    <span className="text-green-600">
                                      Promo: R$ {Number(item.promotionPrice).toFixed(2)}
                                    </span>
                                  )}
                                <span className="font-medium">
                                  Subtotal: R$ {subtotal.toFixed(2)}
                                </span>
                              </div>
                              {item.observation && (
                                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                  Obs: {item.observation}
                                </p>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                removeItem(typeof item.id === "number" ? item.id : Number(item.id))
                              }
                              className="p-2 ml-4 text-red-500 transition-all duration-200 rounded-md hover:bg-red-50 dark:hover:bg-red-800/20 hover:scale-110 active:scale-95"
                              title="Remover item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div className="p-3 rounded-md bg-slate-50 dark:bg-zink-800">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Total de Produtos:
                        </span>
                        <span className="font-medium">
                          R$ {validation.values.amountProducts || "0.00"}
                        </span>
                      </div>
                      {Number(validation.values.amountPromotion) > 0 && (
                        <div className="flex items-center justify-between mt-1 text-sm">
                          <span className="text-slate-600 dark:text-slate-400">
                            Desconto Total:
                          </span>
                          <span className="font-medium text-green-600">
                            -R$ {validation.values.amountPromotion || "0.00"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {validation.touched.items &&
                  validation.errors.items &&
                  typeof validation.errors.items === "string" && (
                    <div className="p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md dark:bg-red-800/10 dark:border-red-500/20 dark:text-red-400">
                      {validation.errors.items}
                    </div>
                  )}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row mt-6 mb-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white transition-all duration-200 sm:w-auto btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:scale-105 active:scale-95 disabled:hover:scale-100"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-2 -ml-1 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {isEdit ? "Atualizando..." : "Salvando..."}
                    </div>
                  ) : (
                    (() => {
                      return isEdit ? "Atualizar Pedido" : "Salvar Pedido";
                    })()
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full transition-all duration-200 sm:w-auto text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:border-zink-600 dark:hover:border-zink-500 dark:text-zink-200 dark:ring-zink-400/10 hover:scale-105 active:scale-95"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default OrderForm;
